import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentAdmin, logout, getAuthHeaders } from '../utils/auth';

export const ProjectsAdmin = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [projects, setProjects] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    link: '',
    tech: '',
    description: '',
    image: '',
    status: '',
    published: false,
    order: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const currentAdmin = await getCurrentAdmin();
    if (!currentAdmin) {
      navigate('/login');
      return;
    }
    setAdmin(currentAdmin);
    loadProjects();
  };

  const loadProjects = async () => {
    try {
      const response = await fetch('/api/projects', {
        headers: getAuthHeaders(),
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to load projects');

      const data = await response.json();
      setProjects(data.projects || []);
      setLoading(false);
    } catch (error) {
      console.error('Error loading projects:', error);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const url = '/api/projects';
      const method = currentProject ? 'PUT' : 'POST';

      const payload = currentProject
        ? { ...formData, id: currentProject.id }
        : formData;

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        alert(`Error: ${error.error}`);
        return;
      }

      alert(`Project ${currentProject ? 'updated' : 'created'} successfully!`);
      resetForm();
      loadProjects();
    } catch (error) {
      console.error('Error saving project:', error);
      alert('Error saving project');
    }
  };

  const editProject = (project) => {
    setCurrentProject(project);
    setFormData({
      name: project.name,
      link: project.link || '',
      tech: project.tech,
      description: project.description,
      image: project.image || '',
      status: project.status || '',
      published: project.published,
      order: project.order
    });
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      const response = await fetch('/api/projects', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        credentials: 'include',
        body: JSON.stringify({ id })
      });

      if (!response.ok) {
        const error = await response.json();
        alert(`Error: ${error.error}`);
        return;
      }

      alert('Project deleted successfully!');
      loadProjects();
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Error deleting project');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      link: '',
      tech: '',
      description: '',
      image: '',
      status: '',
      published: false,
      order: 0
    });
    setCurrentProject(null);
    setIsEditing(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-custom-background flex items-center justify-center">
        <p className="text-zinc-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-custom-background py-20 px-10 lg:px-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-grandslang text-[60px] text-charcoal">
              Admin Panel
            </h1>
            <p className="text-zinc-500">Welcome, {admin?.name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="px-6 py-2 border border-charcoal text-charcoal hover:bg-charcoal hover:text-white transition-colors"
          >
            Logout
          </button>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-4 mb-8 border-b border-charcoal/20">
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-3 text-charcoal/50 hover:text-charcoal border-b-2 border-transparent hover:border-charcoal/30"
          >
            Blogs
          </button>
          <button
            onClick={() => navigate('/admin/gallery')}
            className="px-6 py-3 text-charcoal/50 hover:text-charcoal border-b-2 border-transparent hover:border-charcoal/30"
          >
            Gallery
          </button>
          <button
            onClick={() => navigate('/admin/projects')}
            className="px-6 py-3 text-charcoal border-b-2 border-charcoal font-medium"
          >
            Projects
          </button>
        </div>

        {/* Project Form */}
        <div className="bg-white p-8 mb-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-6 text-charcoal">
            {currentProject ? 'Edit Project' : 'Create New Project'}
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Project Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="e.g., HRMS, KreditAfrica"
              />
            </div>

            {/* Tech Stack */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Tech Stack *
              </label>
              <input
                type="text"
                name="tech"
                value={formData.tech}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="e.g., React / GraphQL, Next.js / Typescript"
              />
            </div>

            {/* Link */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Project Link (URL)
              </label>
              <input
                type="url"
                name="link"
                value={formData.link}
                onChange={handleInputChange}
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="https://example.com"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows="6"
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="Detailed project description..."
              />
            </div>

            {/* Image URL */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Image URL
              </label>
              <input
                type="text"
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="https://example.com/image.jpg or Cloudinary public ID"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Status
              </label>
              <input
                type="text"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="e.g., In Progress, Completed, Live"
              />
            </div>

            {/* Order */}
            <div>
              <label className="block text-sm font-medium text-charcoal mb-2">
                Display Order (0 = first)
              </label>
              <input
                type="number"
                name="order"
                value={formData.order}
                onChange={handleInputChange}
                className="w-full p-3 border border-charcoal/30 focus:border-charcoal focus:outline-none"
                placeholder="0"
              />
            </div>

            {/* Published */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleInputChange}
                className="mr-2 w-5 h-5"
              />
              <label className="text-sm font-medium text-charcoal">
                Publish this project
              </label>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                type="submit"
                className="px-6 py-3 bg-charcoal text-white hover:bg-charcoal/90 transition-colors"
              >
                {currentProject ? 'Update Project' : 'Create Project'}
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 border border-charcoal text-charcoal hover:bg-charcoal/10"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Projects List */}
        <div className="bg-white p-8 shadow-lg">
          <h2 className="text-2xl font-bold mb-6 text-charcoal">
            All Projects ({projects.length})
          </h2>

          {projects.length === 0 ? (
            <p className="text-charcoal/70">No projects yet. Create your first one above!</p>
          ) : (
            <div className="space-y-4">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="border border-charcoal/20 p-4 flex justify-between items-start"
                >
                  <div className="flex-1">
                    <h3 className="font-grandslang text-xl text-charcoal mb-2">
                      {project.name}
                      {!project.published && (
                        <span className="ml-3 text-sm text-red-600 font-normal">
                          (Draft)
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-charcoal/70 mb-2">{project.tech}</p>
                    <p className="text-xs text-charcoal/50 line-clamp-2 mb-2">
                      {project.description}
                    </p>
                    <div className="text-xs text-charcoal/50">
                      Order: {project.order} | Created: {new Date(project.createdAt).toLocaleDateString()}
                      {project.link && (
                        <a href={project.link} target="_blank" rel="noopener noreferrer" className="ml-4 text-charcoal underline">
                          View Live →
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => editProject(project)}
                      className="px-4 py-2 bg-charcoal text-white hover:bg-charcoal/90 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(project.id)}
                      className="px-4 py-2 border border-red-600 text-red-600 hover:bg-red-600 hover:text-white text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
